<div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px" data-testid="module-config-field-formula-editor">
    <div fxFlex fxLayout="column" fxLayoutGap="8px" class="cipo--min-width--0">
        @if (loading()) {
            <ngx-skeleton-loader
                count="1"
                role="status"
                [attr.aria-busy]="true"
                [attr.aria-label]="'a11y.moduleConfig.loadingFormula' | translate"
                [theme]="{ width: '100%', height: '96px', margin: '0' }" />
        } @else {
            <div
                #area
                class="formula-area p-a-sm"
                role="group"
                data-testid="module-config-field-formula-editor-area"
                [attr.tabindex]="locked() ? -1 : 0"
                [attr.aria-label]="'modules.fieldConfig.formula.expression' | translate"
                [class.formula-area--focused]="focused()"
                [class.formula-area--disabled]="locked()"
                (focus)="focused.set(true)"
                (blur)="onBlur()"
                (keydown)="onKeydown($event)"
                (click)="moveCaretToEnd()">
                @for (token of tokens(); track $index; let index = $index) {
                    @if (caret() === index) {
                        <ng-container [ngTemplateOutlet]="caretMark" />
                    }
                    <button
                        type="button"
                        tabindex="-1"
                        class="formula-area__token formula-area__token--{{ token.type }}"
                        data-testid="module-config-field-formula-editor-token"
                        [class.formula-area__token--selected]="selected() === index"
                        [class.formula-area__token--error]="invalidIndexes().has(index)"
                        [disabled]="locked()"
                        [attr.aria-pressed]="selected() === index"
                        (mousedown)="$event.preventDefault()"
                        (click)="$event.stopPropagation(); select(index)">
                        {{ chipText(token) }}
                    </button>
                }
                @if (caret() === tokens().length) {
                    <ng-container [ngTemplateOutlet]="caretMark" />
                }
                @if (!tokens().length && !pending()) {
                    <span class="mat-body-2 cipo-text--black-500">
                        {{ "modules.fieldConfig.formula.editorHint" | translate }}
                    </span>
                }
            </div>
            <ng-template #caretMark>
                <span class="formula-area__caret" aria-hidden="true">{{ pending() }}</span>
            </ng-template>

            <div fxLayout="row wrap" class="cipo--gap--xs">
                @for (symbol of symbols; track symbol.value) {
                    <button
                        mat-stroked-button
                        type="button"
                        class="formula-key"
                        data-testid="module-config-field-formula-editor-symbol"
                        [attr.data-symbol]="symbol.value"
                        [disabled]="locked() || !insertableSymbols().has(symbol.value)"
                        (click)="insertSymbol(symbol.value)">
                        {{ symbol.label }}
                    </button>
                }
                <button
                    mat-stroked-button
                    type="button"
                    class="formula-key"
                    data-testid="module-config-field-formula-editor-backspace"
                    [disabled]="locked() || (!caret() && selected() === null && !pending())"
                    [attr.aria-label]="'actions.delete' | translate"
                    (click)="backspace()">
                    <mat-icon class="m-a-0" aria-hidden="true">backspace</mat-icon>
                </button>
            </div>

            <cipo-formula-status [tokens]="tokens()" [isDateResult]="isDateResult()" />

            @if (selectedToken()) {
                <section
                    class="formula-selected p-a-sm"
                    fxLayout="column"
                    fxLayoutGap="8px"
                    data-testid="module-config-field-formula-editor-selected"
                    [attr.aria-label]="'modules.fieldConfig.formula.selected' | translate">
                    <div fxLayout="row" fxLayoutAlign="space-between center">
                        <span class="mat-body-2 cipo--text-weight--medium">
                            {{ "modules.fieldConfig.formula.selected" | translate }}
                        </span>
                        <button
                            mat-icon-button
                            type="button"
                            data-testid="module-config-field-formula-editor-delete-selected"
                            [disabled]="locked()"
                            [attr.aria-label]="'actions.delete' | translate"
                            (click)="deleteToken(selected())">
                            <mat-icon aria-hidden="true">delete</mat-icon>
                        </button>
                    </div>

                    <div class="formula-selected__row cipo--gap--md">
                        @if (selectedFunction(); as token) {
                            <mat-button-toggle-group
                                hideSingleSelectionIndicator
                                [value]="token.functionKey"
                                [disabled]="locked()"
                                [attr.aria-label]="'modules.fieldConfig.formula.groups.functions' | translate"
                                (change)="setFunction(selected(), $event.value)">
                                @for (entry of functions; track entry.key) {
                                    <mat-button-toggle [value]="entry.key">{{ entry.label }}</mat-button-toggle>
                                }
                            </mat-button-toggle-group>
                            <mat-form-field class="cipo-input formula-selected__field" subscriptSizing="dynamic">
                                <mat-label>{{ "modules.fieldConfig.formula.selectColumn" | translate }}</mat-label>
                                <mat-select
                                    data-testid="module-config-field-formula-editor-selected-field"
                                    [value]="token.field"
                                    [compareWith]="sameField"
                                    [disabled]="locked()"
                                    (selectionChange)="setField(selected(), 'field', $event.value)">
                                    @for (field of numericFields(); track field.key) {
                                        <mat-option
                                            data-testid="module-config-field-formula-editor-field-option"
                                            [attr.data-field-key]="field.key"
                                            [value]="field">
                                            {{ field.value }}
                                        </mat-option>
                                    }
                                </mat-select>
                            </mat-form-field>
                        }

                        @if (selectedDate(); as token) {
                            <mat-button-toggle-group
                                hideSingleSelectionIndicator
                                [value]="token.datePart"
                                [disabled]="locked()"
                                [attr.aria-label]="'modules.fieldConfig.formula.groups.dateParts' | translate"
                                (change)="setDatePart(selected(), $event.value)">
                                @for (part of dateParts; track part) {
                                    <mat-button-toggle [value]="part">
                                        {{ "modules.fieldConfig.formula.dateParts." + part | translate }}
                                    </mat-button-toggle>
                                }
                            </mat-button-toggle-group>
                            @if (token.secondKind === "number") {
                                <mat-button-toggle-group
                                    hideSingleSelectionIndicator
                                    [value]="token.useSecondField ? 'field' : 'number'"
                                    [disabled]="locked()"
                                    [attr.aria-label]="'modules.fieldConfig.formula.selectNumber' | translate"
                                    (change)="useSecondField(selected(), $event.value === 'field')">
                                    <mat-button-toggle
                                        value="number"
                                        data-testid="module-config-field-formula-editor-second-kind-number">
                                        {{ "modules.fieldConfig.formula.number" | translate }}
                                    </mat-button-toggle>
                                    <mat-button-toggle
                                        value="field"
                                        data-testid="module-config-field-formula-editor-second-kind-field">
                                        {{ "modules.fieldConfig.formula.groups.fields" | translate }}
                                    </mat-button-toggle>
                                </mat-button-toggle-group>
                            }
                            @if (token.secondKind === "date" || token.useSecondField) {
                                <mat-form-field class="cipo-input formula-selected__field" subscriptSizing="dynamic">
                                    <mat-label>
                                        {{
                                            (token.secondKind === "date"
                                                ? "modules.fieldConfig.formula.selectStartDate"
                                                : "modules.fieldConfig.formula.selectNumber"
                                            ) | translate
                                        }}
                                    </mat-label>
                                    <mat-select
                                        data-testid="module-config-field-formula-editor-selected-second"
                                        [value]="token.secondField"
                                        [compareWith]="sameField"
                                        [disabled]="locked()"
                                        (selectionChange)="setField(selected(), 'second', $event.value)">
                                        @for (field of fieldsFor("second", token); track field.key) {
                                            <mat-option
                                                data-testid="module-config-field-formula-editor-field-option"
                                                [attr.data-field-key]="field.key"
                                                [value]="field">
                                                {{ field.value }}
                                            </mat-option>
                                        }
                                    </mat-select>
                                </mat-form-field>
                            } @else {
                                <mat-form-field class="cipo-input formula-selected__field" subscriptSizing="dynamic">
                                    <mat-label>{{ "modules.fieldConfig.formula.number" | translate }}</mat-label>
                                    <input
                                        matInput
                                        type="number"
                                        min="0"
                                        data-testid="module-config-field-formula-editor-selected-second-number"
                                        [value]="token.secondNumber ?? ''"
                                        [disabled]="locked()"
                                        (input)="setSecondNumber(selected(), toNumber($any($event.target).value))" />
                                </mat-form-field>
                            }
                            <mat-form-field class="cipo-input formula-selected__field" subscriptSizing="dynamic">
                                <mat-label>
                                    {{
                                        (token.secondKind === "date"
                                            ? "modules.fieldConfig.formula.selectEndDate"
                                            : "modules.fieldConfig.formula.selectDate"
                                        ) | translate
                                    }}
                                </mat-label>
                                <mat-select
                                    data-testid="module-config-field-formula-editor-selected-third"
                                    [value]="token.thirdField"
                                    [compareWith]="sameField"
                                    [disabled]="locked()"
                                    (selectionChange)="setField(selected(), 'third', $event.value)">
                                    @for (field of fieldsFor("third", token); track field.key) {
                                        <mat-option
                                            data-testid="module-config-field-formula-editor-field-option"
                                            [attr.data-field-key]="field.key"
                                            [value]="field">
                                            {{ field.value }}
                                        </mat-option>
                                    }
                                </mat-select>
                            </mat-form-field>
                        }

                        @if (selectedOperator(); as token) {
                            <mat-button-toggle-group
                                hideSingleSelectionIndicator
                                [value]="token.value"
                                [disabled]="locked()"
                                [attr.aria-label]="'modules.fieldConfig.formula.operator' | translate"
                                (change)="setOperator(selected(), $event.value)">
                                @for (operator of operatorsAt(selected()); track operator.value) {
                                    <mat-button-toggle [value]="operator.value">{{ operator.label }}</mat-button-toggle>
                                }
                            </mat-button-toggle-group>
                        }

                        @if (selectedNumber(); as token) {
                            <mat-form-field class="cipo-input formula-selected__field" subscriptSizing="dynamic">
                                <mat-label>{{ "modules.fieldConfig.formula.number" | translate }}</mat-label>
                                <input
                                    matInput
                                    type="number"
                                    min="0"
                                    data-testid="module-config-field-formula-editor-selected-number"
                                    [value]="token.value"
                                    [disabled]="locked()"
                                    (input)="setNumber(selected(), toNumber($any($event.target).value) ?? 0)" />
                            </mat-form-field>
                        }
                    </div>
                </section>
            }
        }

        @if (disabled()) {
            <span
                class="mat-caption cipo-text--black-500"
                fxLayout="row"
                fxLayoutAlign="start center"
                fxLayoutGap="4px">
                <mat-icon class="material-icons-outlined" inline aria-hidden="true">info</mat-icon>
                {{ "modules.fieldConfig.formula.onlyOnDefault" | translate }}
            </span>
        }
    </div>

    <aside
        fxFlex="320px"
        fxFlex.lt-md="100"
        fxLayout="column"
        fxLayoutGap="8px"
        class="cipo-outlined-field p-a-sm cipo--min-width--0"
        data-testid="module-config-field-formula-editor-panel">
        <mat-form-field class="cipo-input" subscriptSizing="dynamic">
            <mat-label>{{ "modules.fieldConfig.formula.searchInsert" | translate }}</mat-label>
            <input
                #searchBox
                matInput
                data-testid="module-config-field-formula-editor-search"
                [value]="search()"
                [disabled]="locked()"
                (input)="search.set($any($event.target).value)"
                (keydown)="onSearchKeydown($event)" />
            <mat-icon matSuffix aria-hidden="true">search</mat-icon>
        </mat-form-field>

        <mat-action-list class="formula-panel__lists cipo--overflow-y--auto">
            @for (group of fieldGroups(); track group.label) {
                <div mat-subheader>{{ group.label || ("modules.fieldConfig.formula.groups.fields" | translate) }}</div>
                @for (entry of group.fields; track entry.field.key) {
                    <button
                        mat-list-item
                        type="button"
                        data-testid="module-config-field-formula-editor-field"
                        [attr.data-field-key]="entry.field.key"
                        [disabled]="locked() || !acceptsValue()"
                        [title]="entry.field.value"
                        (click)="insertField(entry.field)">
                        <mat-icon matListItemIcon aria-hidden="true">tag</mat-icon>
                        <span matListItemTitle>{{ entry.label }}</span>
                    </button>
                }
            }

            @if (visibleFunctions().length) {
                <div mat-subheader>{{ "modules.fieldConfig.formula.groups.functions" | translate }}</div>
                @for (entry of visibleFunctions(); track entry.key) {
                    <button
                        mat-list-item
                        type="button"
                        data-testid="module-config-field-formula-editor-function"
                        [disabled]="locked() || !acceptsValue()"
                        (click)="chooseFunction(entry.key)">
                        <mat-icon matListItemIcon aria-hidden="true">functions</mat-icon>
                        <span matListItemTitle>{{ entry.label }}</span>
                        <span matListItemLine>{{ "modules.fieldConfig.formula.help." + entry.value | translate }}</span>
                    </button>
                }
            }

            @if (showDateFunction()) {
                <div mat-subheader>{{ "modules.fieldConfig.formula.groups.dateFunctions" | translate }}</div>
                <button
                    mat-list-item
                    type="button"
                    data-testid="module-config-field-formula-editor-date-function"
                    [disabled]="locked() || !allowed().date"
                    (click)="insertDateFunction()">
                    <mat-icon matListItemIcon aria-hidden="true">event</mat-icon>
                    <span matListItemTitle>{{ dateFunction() }}</span>
                    <span matListItemLine>{{ "modules.fieldConfig.formula.help." + dateFunction() | translate }}</span>
                </button>
            }
        </mat-action-list>
    </aside>
</div>
